import React from 'react';

interface StrikeRow {
  strike: number;
  call_oi: number;
  put_oi: number;
  call_ltp?: number;
  put_ltp?: number;
  call_volume?: number;
  put_volume?: number;
}

interface OIChain {
  spot_price?: number;
  max_pain?: number;
  pcr?: number;
  expiry_date?: string;
  captured_at?: string;
  strikes: StrikeRow[];
}

interface OILevels {
  spot_price?: number;
  resistance?: number[];
  support?: number[];
  max_pain?: number;
  pcr?: number;
}

interface Props {
  symbol: string;
  oiLevels: OILevels;
  oiChain?: OIChain | null;
}

function fmt(n?: number) {
  if (!n) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

function OIDistChart({ symbol, oiChain }: { symbol: string; oiChain: OIChain }) {
  const { strikes, spot_price, max_pain, pcr, expiry_date, captured_at } = oiChain;

  const maxOI = Math.max(...strikes.flatMap(s => [s.call_oi, s.put_oi]), 1);
  const nearSpot = strikes.length
    ? strikes.reduce((prev, curr) =>
        Math.abs(curr.strike - (spot_price || 0)) < Math.abs(prev.strike - (spot_price || 0))
          ? curr : prev
      ).strike
    : null;

  const fmtTime = (iso?: string) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const topCallStrike = [...strikes].sort((a, b) => b.call_oi - a.call_oi)[0]?.strike;
  const topPutStrike = [...strikes].sort((a, b) => b.put_oi - a.put_oi)[0]?.strike;

  return (
    <div>
      {/* Header info */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={{ color: '#555', fontSize: '0.58rem' }}>PUT OI</span>
          <div style={{ width: 20, height: 2, background: '#26a65b', alignSelf: 'center' }} />
          <span style={{ color: '#555', fontSize: '0.58rem' }}>CALL OI</span>
          <div style={{ width: 20, height: 2, background: '#e74c3c', alignSelf: 'center' }} />
        </div>
        {spot_price && (
          <span style={{ color: '#f39c12', fontSize: '0.62rem' }}>
            ● Spot: <strong>{spot_price.toLocaleString('en-IN')}</strong>
          </span>
        )}
        {max_pain && (
          <span style={{ color: '#e67e22', fontSize: '0.62rem' }}>
            ✦ Max Pain: <strong>{max_pain.toLocaleString('en-IN')}</strong>
          </span>
        )}
        {pcr != null && (
          <span style={{
            color: pcr > 1.2 ? '#26a65b' : pcr < 0.8 ? '#e74c3c' : '#f39c12',
            fontSize: '0.62rem', fontWeight: 700,
          }}>
            PCR: {pcr.toFixed(2)} {pcr > 1.2 ? '▲' : pcr < 0.8 ? '▼' : '—'}
          </span>
        )}
        {topCallStrike && (
          <span style={{ color: '#e74c3c', fontSize: '0.58rem' }}>
            CE wall: {topCallStrike.toLocaleString('en-IN')}
          </span>
        )}
        {topPutStrike && (
          <span style={{ color: '#26a65b', fontSize: '0.58rem' }}>
            PE floor: {topPutStrike.toLocaleString('en-IN')}
          </span>
        )}
        {captured_at && (
          <span style={{ color: '#333', fontSize: '0.58rem', marginLeft: 'auto' }}>
            {expiry_date} · {fmtTime(captured_at)}
          </span>
        )}
      </div>

      {/* Column headers */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '48px 1fr 72px 1fr 48px',
        padding: '3px 4px',
        marginBottom: 2,
      }}>
        <span style={{ color: '#555', fontSize: '0.58rem', textAlign: 'right' }}>P.LTP</span>
        <span style={{ color: '#26a65b', fontSize: '0.58rem', textAlign: 'right', paddingRight: 4 }}>PUT OI</span>
        <span style={{ color: '#888', fontSize: '0.6rem', textAlign: 'center', fontWeight: 700 }}>STRIKE</span>
        <span style={{ color: '#e74c3c', fontSize: '0.58rem', textAlign: 'left', paddingLeft: 4 }}>CALL OI</span>
        <span style={{ color: '#555', fontSize: '0.58rem' }}>C.LTP</span>
      </div>

      {/* Strike rows */}
      <div style={{ maxHeight: 310, overflowY: 'auto', scrollbarWidth: 'thin' }}>
        {strikes.map(row => {
          const isSpot = row.strike === nearSpot;
          const isMaxPain = row.strike === max_pain;
          const isTopCall = row.strike === topCallStrike;
          const isTopPut = row.strike === topPutStrike;
          const callBarW = Math.max(2, Math.round((row.call_oi / maxOI) * 100));
          const putBarW = Math.max(2, Math.round((row.put_oi / maxOI) * 100));

          return (
            <div key={row.strike} style={{
              display: 'grid',
              gridTemplateColumns: '48px 1fr 72px 1fr 48px',
              alignItems: 'center',
              padding: '2px 4px',
              background: isSpot ? '#1c1a0a' : isMaxPain ? '#1a120a' : 'transparent',
              border: `1px solid ${isSpot ? '#f39c1233' : isMaxPain ? '#e67e2222' : 'transparent'}`,
              borderRadius: 3,
              marginBottom: 1,
            }}>
              {/* Put LTP */}
              <span style={{ color: '#555', fontSize: '0.58rem', textAlign: 'right', paddingRight: 4 }}>
                {row.put_ltp ? row.put_ltp.toFixed(0) : '—'}
              </span>

              {/* Put OI bar — right-aligned green */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: 16 }}>
                <div style={{ position: 'relative', width: `${putBarW}%`, maxWidth: '100%', height: 14 }}>
                  <div style={{
                    position: 'absolute', right: 0, top: 0,
                    width: '100%', height: '100%',
                    background: isTopPut ? '#26a65b88' : '#26a65b33',
                    borderRadius: '3px 0 0 3px',
                    borderLeft: isTopPut ? '2px solid #26a65b' : 'none',
                  }} />
                  {putBarW > 25 && (
                    <span style={{
                      position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                      color: '#26a65b', fontSize: '0.52rem', whiteSpace: 'nowrap',
                    }}>
                      {fmt(row.put_oi)}
                    </span>
                  )}
                </div>
              </div>

              {/* Strike */}
              <div style={{ textAlign: 'center', padding: '0 4px' }}>
                <span style={{
                  color: isSpot ? '#f39c12' : isMaxPain ? '#e67e22' : '#999',
                  fontSize: '0.65rem',
                  fontWeight: isSpot || isMaxPain || isTopCall || isTopPut ? 800 : 400,
                }}>
                  {isSpot && <span style={{ fontSize: '0.5rem', marginRight: 2 }}>●</span>}
                  {isMaxPain && !isSpot && <span style={{ fontSize: '0.5rem', marginRight: 2 }}>✦</span>}
                  {row.strike.toLocaleString('en-IN')}
                </span>
              </div>

              {/* Call OI bar — left-aligned red */}
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', height: 16 }}>
                <div style={{ position: 'relative', width: `${callBarW}%`, maxWidth: '100%', height: 14 }}>
                  <div style={{
                    position: 'absolute', left: 0, top: 0,
                    width: '100%', height: '100%',
                    background: isTopCall ? '#e74c3c88' : '#e74c3c33',
                    borderRadius: '0 3px 3px 0',
                    borderRight: isTopCall ? '2px solid #e74c3c' : 'none',
                  }} />
                  {callBarW > 25 && (
                    <span style={{
                      position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)',
                      color: '#e74c3c', fontSize: '0.52rem', whiteSpace: 'nowrap',
                    }}>
                      {fmt(row.call_oi)}
                    </span>
                  )}
                </div>
              </div>

              {/* Call LTP */}
              <span style={{ color: '#555', fontSize: '0.58rem', paddingLeft: 4 }}>
                {row.call_ltp ? row.call_ltp.toFixed(0) : '—'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FallbackLevels({ oiLevels, symbol }: { oiLevels: OILevels; symbol: string }) {
  const { resistance = [], support = [], max_pain, spot_price, pcr } = oiLevels;
  if (!resistance.length && !support.length && !max_pain) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#444', fontSize: '0.72rem' }}>
        No OI data. Kite option chain cron must run during market hours.
      </div>
    );
  }
  return (
    <div style={{ padding: '0 4px' }}>
      {resistance.map((r, i) => (
        <div key={`r${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #111' }}>
          <span style={{ color: '#e74c3c', fontSize: '0.68rem' }}>Resistance {i + 1}</span>
          <span style={{ color: '#ddd', fontWeight: 700 }}>{r?.toLocaleString('en-IN')}</span>
        </div>
      ))}
      {spot_price && (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #111' }}>
          <span style={{ color: '#f39c12', fontWeight: 700 }}>● Spot</span>
          <span style={{ color: '#f39c12', fontWeight: 800 }}>{spot_price?.toLocaleString('en-IN')}</span>
        </div>
      )}
      {max_pain && (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #111' }}>
          <span style={{ color: '#e67e22', fontSize: '0.65rem' }}>Max Pain</span>
          <span style={{ color: '#e67e22', fontWeight: 700 }}>{max_pain?.toLocaleString('en-IN')}</span>
        </div>
      )}
      {support.map((s, i) => (
        <div key={`s${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #111' }}>
          <span style={{ color: '#26a65b', fontSize: '0.68rem' }}>Support {i + 1}</span>
          <span style={{ color: '#ddd', fontWeight: 700 }}>{s?.toLocaleString('en-IN')}</span>
        </div>
      ))}
      {pcr != null && (
        <div style={{ marginTop: 6, color: pcr > 1.2 ? '#26a65b' : pcr < 0.8 ? '#e74c3c' : '#f39c12', fontSize: '0.68rem', fontWeight: 700 }}>
          PCR: {pcr.toFixed(2)}
        </div>
      )}
    </div>
  );
}

export default function OILevelsChart({ symbol, oiLevels, oiChain }: Props) {
  if (oiChain?.strikes?.length) {
    return <OIDistChart symbol={symbol} oiChain={oiChain} />;
  }
  return <FallbackLevels oiLevels={oiLevels} symbol={symbol} />;
}
